import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

/* ------------------------ helpers ------------------------ */
const sameId = (a, b) => String(a || "") === String(b || "");
const toStr = (x) => (x == null ? "" : String(x));

// list filter matcher for optimistic patches
const matchesFilters = (item, args = {}) => {
  const {
    q = "",
    branchId = "",
    counterId = "",
    vendorId = "",
    stoneId = "",
    shape = "",
    color = "",
    size = "",
    settingType = "",
    dateFrom = "",
    dateTo = "",
  } = args || {};

  // ids
  if (branchId && !sameId(item.branchId, branchId)) return false;
  if (counterId && !sameId(item.counterId, counterId)) return false;
  if (vendorId && !sameId(item.vendorId, vendorId)) return false;
  if (stoneId && !sameId(item.stoneId, stoneId)) return false;
  if (shape && !sameId(item.shape, shape)) return false;
  if (color && !sameId(item.color, color)) return false;
  if (size && !sameId(item.size, size)) return false;
  if (settingType && !sameId(item.settingType, settingType)) return false;

  // date window
  if (dateFrom && new Date(item.createdAt) < new Date(dateFrom)) return false;
  if (dateTo && new Date(item.createdAt) > new Date(dateTo)) return false;

  // text search
  if (q) {
    const needle = toStr(q).toLowerCase();
    const hay = [
      item.stoneName,
      item.shapeName,
      item.colorName,
      item.sizeName,
      item.settingTypeName,
      item.branchName,
      item.counterName,   // 🔥 include counter in search
      item.vendorName,
      item.description,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!hay.includes(needle)) return false;
  }
  return true;
};

// list-cache total helpers (supports {pagination:{}} or {total,pages})
const incrementTotals = (draft, by = 1) => {
  if (draft?.pagination?.totalDocs != null) {
    draft.pagination.totalDocs += by;
    const lim = draft?.pagination?.limit || draft?.limit || 10;
    draft.pagination.totalPages = Math.max(1, Math.ceil(draft.pagination.totalDocs / lim));
  } else if (draft?.total != null) {
    draft.total += by;
    const lim = draft?.limit || draft?.pagination?.limit || 10;
    draft.pages = Math.max(1, Math.ceil(draft.total / lim));
  }
};
const decrementTotals = (draft, by = 1) => {
  if (draft?.pagination?.totalDocs != null) {
    draft.pagination.totalDocs = Math.max(0, draft.pagination.totalDocs - by);
    const lim = draft?.pagination?.limit || draft?.limit || 10;
    draft.pagination.totalPages = Math.max(1, Math.ceil(draft.pagination.totalDocs / lim));
  } else if (draft?.total != null) {
    draft.total = Math.max(0, draft.total - by);
    const lim = draft?.limit || draft?.pagination?.limit || 10;
    draft.pages = Math.max(1, Math.ceil(draft.total / lim));
  }
};

// first page insert helper
const addItemsIntoListDraft = (draft, args, newItems = []) => {
  if (!Array.isArray(draft?.data)) return;
  if (args.page && Number(args.page) !== 1) {
    incrementTotals(draft, newItems.length);
    return;
  }
  draft.data.unshift(...newItems);
  if (args.limit) draft.data.splice(Number(args.limit));
  incrementTotals(draft, newItems.length);
};

/* ========================= API ========================= */
export const looseStoneApi = createApi({
  reducerPath: "looseStoneApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    credentials: "include",
  }),
  tagTypes: ["LooseStoneEntry", "LooseStoneReport"],
  endpoints: (builder) => ({
    /* ------------ LIST ------------ */
    // GET /loose-stones
    getLooseStoneEntries: builder.query({
      query: (args = {}) => {
        const {
          page = 1,
          limit = 20,
          q = "",
          branchId = "",
          counterId = "", // 🔥 NEW
          vendorId = "",
          stoneId = "",
          shape = "",
          color = "",
          size = "",
          settingType = "",
          dateFrom = "",
          dateTo = "",
          sortBy = "createdAt",
          sortOrder = "desc",
        } = args;

        const params = {
          page,
          limit,
          q,
          branchId,
          counterId, // 🔥 pass to server
          vendorId,
          stoneId,
          shape,
          color,
          size,
          settingType,
          dateFrom,
          dateTo,
          sortBy,
          sortOrder,
        };

        Object.keys(params).forEach((k) => (params[k] ? null : delete params[k]));
        return { url: "/loose-stones", method: "GET", params };
      },
      providesTags: ["LooseStoneEntry"],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const a = queryArgs || {};
        return [
          endpointName,
          a.page,
          a.limit,
          a.q,
          a.branchId,
          a.counterId, // 🔥 so each filter combo has its own cache key
          a.vendorId,
          a.stoneId,
          a.shape,
          a.color,
          a.size,
          a.settingType,
          a.dateFrom,
          a.dateTo,
          a.sortBy,
          a.sortOrder,
        ].join("|");
      },
      keepUnusedDataFor: 60,
    }),

    /* ------------ GET ONE ------------ */
    // GET /loose-stones/:id
    getLooseStoneEntryById: builder.query({
      query: (id) => `/loose-stones/${id}`,
      providesTags: (_res, _err, id) => [{ type: "LooseStoneEntry", id }],
    }),

    /* ------------ CREATE (single or bulk) ------------ */
    // POST /loose-stones/create   body: { items:[...] } or single
    addLooseStoneEntry: builder.mutation({
      query: (payload) => {
        const items = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.items)
          ? payload.items
          : [payload];
        return {
          url: "/loose-stones/create",
          method: "POST",
          body: { items },
        };
      },
      // optimistic: push to every matching cached list
      async onQueryStarted(payload, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: res } = await queryFulfilled;
          const created = Array.isArray(res?.data) ? res.data : [];
          if (!created.length) return;
          const allArgs = looseStoneApi.util.selectCachedArgsForQuery(
            getState(),
            "getLooseStoneEntries"
          );
          for (const args of allArgs) {
            const addable = created.filter((it) => matchesFilters(it, args));
            if (!addable.length) continue;
            dispatch(
              looseStoneApi.util.updateQueryData("getLooseStoneEntries", args, (draft) => {
                addItemsIntoListDraft(draft, args, addable);
              })
            );
          }
        } catch {
          /* ignore */
        }
      },
    }),

    /* ------------ UPDATE ------------ */
    // PUT /loose-stones/:id
    updateLooseStoneEntry: builder.mutation({
      query: ({ id, payload }) => ({
        url: `/loose-stones/${id}`,
        method: "PUT",
        body: payload,
      }),
      async onQueryStarted({ id, payload }, { dispatch, queryFulfilled, getState }) {
        const patches = [];

        // lists
        const allArgs = looseStoneApi.util.selectCachedArgsForQuery(
          getState(),
          "getLooseStoneEntries"
        );
        for (const args of allArgs) {
          patches.push(
            dispatch(
              looseStoneApi.util.updateQueryData("getLooseStoneEntries", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const i = draft.data.findIndex((x) => String(x._id) === String(id));
                if (i >= 0) draft.data[i] = { ...draft.data[i], ...payload };
              })
            )
          );
        }

        // detail
        patches.push(
          dispatch(
            looseStoneApi.util.updateQueryData("getLooseStoneEntryById", id, (draft) => {
              if (draft?.data) Object.assign(draft.data, payload);
            })
          )
        );

        try {
          const { data: res } = await queryFulfilled;
          const updated = res?.data;
          if (!updated) return;

          for (const args of allArgs) {
            dispatch(
              looseStoneApi.util.updateQueryData("getLooseStoneEntries", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const i = draft.data.findIndex((x) => String(x._id) === String(id));
                if (i >= 0) draft.data[i] = { ...draft.data[i], ...updated };
              })
            );
          }
          dispatch(
            looseStoneApi.util.updateQueryData("getLooseStoneEntryById", id, (draft) => {
              if (draft?.data) Object.assign(draft.data, updated);
            })
          );
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    /* ------------ DELETE ------------ */
    // DELETE /loose-stones/:id
    deleteLooseStoneEntry: builder.mutation({
      query: (id) => ({ url: `/loose-stones/${id}`, method: "DELETE" }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const patches = [];
        const allArgs = looseStoneApi.util.selectCachedArgsForQuery(
          getState(),
          "getLooseStoneEntries"
        );
        for (const args of allArgs) {
          patches.push(
            dispatch(
              looseStoneApi.util.updateQueryData("getLooseStoneEntries", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const before = draft.data.length;
                draft.data = draft.data.filter((it) => String(it._id) !== String(id));
                const removed = before - draft.data.length;
                if (removed > 0) decrementTotals(draft, removed);
              })
            )
          );
        }
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    /* ------------ BULK DELETE ------------ */
    // POST /loose-stones/bulk/delete  { ids:[...] }
    bulkDeleteLooseStoneEntries: builder.mutation({
      query: (ids) => ({
        url: `/loose-stones/bulk/delete`,
        method: "POST",
        body: { ids },
      }),
      async onQueryStarted(ids, { dispatch, queryFulfilled, getState }) {
        const patches = [];
        const allArgs = looseStoneApi.util.selectCachedArgsForQuery(
          getState(),
          "getLooseStoneEntries"
        );
        for (const args of allArgs) {
          patches.push(
            dispatch(
              looseStoneApi.util.updateQueryData("getLooseStoneEntries", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const before = draft.data.length;
                draft.data = draft.data.filter((it) => !ids.includes(String(it._id)));
                const removed = before - draft.data.length;
                if (removed > 0) decrementTotals(draft, removed);
              })
            )
          );
        }
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    /* ------------ REPORT ------------ */
    getLooseStoneReport: builder.query({
      query: (params) => ({
        url: "/loose-stones/report",
        method: "GET",
        params,
      }),
      transformResponse: (res) => res?.data || res,
      providesTags: [{ type: "LooseStoneReport", id: "LIST" }],
    }),
  }),
});

export const {
  useGetLooseStoneEntriesQuery,
  useGetLooseStoneReportQuery,
  useGetLooseStoneEntryByIdQuery,
  useAddLooseStoneEntryMutation,
  useUpdateLooseStoneEntryMutation,
  useDeleteLooseStoneEntryMutation,
  useBulkDeleteLooseStoneEntriesMutation,
} = looseStoneApi;

export default looseStoneApi;
